import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  startSlackInstall,
  listSlackWorkspaces,
  listSlackChannels,
  disconnectSlackWorkspace,
  sendSlackTestMessage,
} from "@/lib/integrations/slack.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Send, Slack as SlackIcon } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/slack")({ component: SlackAdmin });

type Workspace = {
  id: string;
  team_id: string;
  team_name: string | null;
  bot_user_id: string | null;
  scopes: string | null;
  installed_at: string;
};

function SlackAdmin() {
  const qc = useQueryClient();
  const fetchWs = useServerFn(listSlackWorkspaces);
  const installFn = useServerFn(startSlackInstall);
  const channelsFn = useServerFn(listSlackChannels);
  const disconnectFn = useServerFn(disconnectSlackWorkspace);
  const testFn = useServerFn(sendSlackTestMessage);

  const { data, isLoading } = useQuery({
    queryKey: ["slack-workspaces"],
    queryFn: () => fetchWs({}),
  });

  const [picked, setPicked] = useState<Record<string, string>>({});

  async function install() {
    try {
      const { authorize_url } = await installFn({ data: {} });
      window.location.href = authorize_url;
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const workspaces = (data?.workspaces ?? []) as Workspace[];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Slack</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Connect your Slack workspace so CairnOS can post operational alerts (low stock, EDI failures,
            ship events) into channels and let your team acknowledge them inline.
          </p>
        </div>
        <Button onClick={install}>
          <SlackIcon className="w-4 h-4 mr-2" /> Add to Slack
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connected Workspaces</CardTitle>
          <CardDescription>
            One row per Slack workspace this tenant has installed CairnOS into.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : workspaces.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No workspaces yet. Click <em>Add to Slack</em> to install.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Bot user</TableHead>
                  <TableHead>Installed</TableHead>
                  <TableHead>Test channel</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaces.map((w) => (
                  <WorkspaceRow
                    key={w.id}
                    w={w}
                    pickedChannel={picked[w.id] ?? ""}
                    onPickChannel={(c) => setPicked((p) => ({ ...p, [w.id]: c }))}
                    onLoadChannels={() => channelsFn({ data: { workspace_id: w.id } })}
                    onTest={async () => {
                      const channelId = picked[w.id];
                      if (!channelId) return toast.error("Pick a channel first");
                      try {
                        await testFn({ data: { workspace_id: w.id, channel_id: channelId } });
                        toast.success("Test message sent");
                      } catch (e) {
                        toast.error((e as Error).message);
                      }
                    }}
                    onDisconnect={async () => {
                      if (!confirm(`Disconnect ${w.team_name ?? w.team_id}?`)) return;
                      try {
                        await disconnectFn({ data: { workspace_id: w.id } });
                        qc.invalidateQueries({ queryKey: ["slack-workspaces"] });
                        toast.success("Disconnected");
                      } catch (e) {
                        toast.error((e as Error).message);
                      }
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WorkspaceRow({
  w,
  pickedChannel,
  onPickChannel,
  onLoadChannels,
  onTest,
  onDisconnect,
}: {
  w: Workspace;
  pickedChannel: string;
  onPickChannel: (id: string) => void;
  onLoadChannels: () => Promise<{ channels: Array<{ id: string; name: string; is_private: boolean }> }>;
  onTest: () => void;
  onDisconnect: () => void;
}) {
  const { data: channelsData, isFetching, refetch } = useQuery({
    queryKey: ["slack-channels", w.id],
    queryFn: onLoadChannels,
    enabled: false,
  });
  const channels = channelsData?.channels ?? [];
  return (
    <TableRow>
      <TableCell className="font-medium">
        {w.team_name ?? "(unnamed)"}
        <div className="text-xs text-muted-foreground font-mono">{w.team_id}</div>
      </TableCell>
      <TableCell className="text-xs font-mono">{w.bot_user_id ?? "—"}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(w.installed_at).toLocaleString()}
      </TableCell>
      <TableCell>
        {channels.length === 0 ? (
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Loading…" : "Load channels"}
          </Button>
        ) : (
          <Select value={pickedChannel} onValueChange={onPickChannel}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Select channel" /></SelectTrigger>
            <SelectContent>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.is_private ? "🔒 " : "# "}{c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button variant="ghost" size="sm" onClick={onTest} title="Send test message">
          <Send className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDisconnect} title="Disconnect">
          <Trash2 className="w-4 h-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}